/**
 * De qué hoja viene cada columna de una fuente. En una hoja es directo; en una unión se
 * compara con el esquema de cada lado y con los sufijos que agrega el motor a los nombres
 * repetidos (`_<etiqueta de la hoja>`, ver _safe_col_suffix en query_engine.py). Los colores
 * son los del mapa de uniones (colorForSource), para que cada hoja se reconozca igual en todo el Creador.
 */
import { colorForSource } from './JoinBlendCanvas';
import { sourceLabel } from './engineConfig';

/** Mismo sufijo que el motor pone a las columnas repetidas de una unión. */
export function safeColSuffix(label) {
    const raw = String(label || '').trim().replace(/[^\p{L}\p{N}_]+/gu, '_').replace(/^_+|_+$/g, '');
    return raw.slice(0, 18) || 'src';
}

function sheetColumns(sourceId, schemasBySource) {
    return (schemasBySource?.[sourceId]?.columns || []).map(col => col.name);
}

/**
 * Mapa columna → ids de las hojas de donde sale. Una clave de unión con el mismo nombre en
 * ambos lados queda con las dos hojas. Las columnas que no se pueden deducir no aparecen.
 */
export function columnOrigins(sourceId, catalog = {}, seen = new Set()) {
    const { dataSources = [], joins = [], schemasBySource = {} } = catalog;
    const out = new Map();
    const source = dataSources.find(item => item.id === sourceId);
    if (!source || seen.has(sourceId)) return out;
    seen.add(sourceId);
    if (source.kind !== 'join') {
        sheetColumns(sourceId, schemasBySource).forEach(name => out.set(name, [sourceId]));
        return out;
    }
    const join = joins.find(item => item.id === source.joinId);
    if (!join) return out;
    const leftId = join.leftDataSourceId;
    const rightId = join.rightDataSourceId;
    const left = columnOrigins(leftId, catalog, new Set(seen));
    const right = columnOrigins(rightId, catalog, new Set(seen));
    const byId = Object.fromEntries(dataSources.map(item => [item.id, item]));
    const leftSuffix = `_${safeColSuffix(byId[leftId]?.label || 'izq')}`;
    let rightSuffixRaw = safeColSuffix(byId[rightId]?.label || 'der');
    if (`_${rightSuffixRaw}` === leftSuffix) rightSuffixRaw = `${rightSuffixRaw}2`;
    const rightSuffix = `_${rightSuffixRaw}`;
    const names = sheetColumns(sourceId, schemasBySource);
    const candidates = names.length ? names : [...new Set([...left.keys(), ...right.keys()])];
    candidates.forEach(name => {
        const inLeft = left.get(name);
        const inRight = right.get(name);
        if (inLeft && inRight) {
            out.set(name, [...new Set([...inLeft, ...inRight])]);
        } else if (inLeft || inRight) {
            out.set(name, inLeft || inRight);
        } else if (name.endsWith(leftSuffix) && left.has(name.slice(0, -leftSuffix.length))) {
            out.set(name, left.get(name.slice(0, -leftSuffix.length)));
        } else if (name.endsWith(rightSuffix) && right.has(name.slice(0, -rightSuffix.length))) {
            out.set(name, right.get(name.slice(0, -rightSuffix.length)));
        }
    });
    return out;
}

/** Datos para pintar una hoja: id, nombre y color. */
export function originInfo(sheetId, catalog = {}) {
    const { dataSources = [], joins = [] } = catalog;
    const source = dataSources.find(item => item.id === sheetId);
    const tone = colorForSource(sheetId, dataSources, joins);
    return { id: sheetId, label: sourceLabel(source), color: tone.stroke, fill: tone.fill, text: tone.text };
}

/**
 * Resumen de la fuente de la paleta: su nombre y, si es una unión, las hojas que la forman
 * (para la cabecera del editor).
 */
export function sourceSummary(sourceId, catalog = {}) {
    const { dataSources = [] } = catalog;
    const source = dataSources.find(item => item.id === sourceId);
    if (!source) return null;
    const sheets = new Set();
    columnOrigins(sourceId, catalog).forEach(ids => ids.forEach(id => sheets.add(id)));
    if (source.kind !== 'join') sheets.add(sourceId);
    return {
        id: sourceId,
        label: sourceLabel(source),
        isJoin: source.kind === 'join',
        sheets: [...sheets].map(id => originInfo(id, catalog)),
    };
}
